import clsx from 'clsx';
import {
  useEffect,
  useId,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useEditorStore } from '../../state/editorStore';

export function PanelShell({
  title,
  children,
  className,
  actions,
  headerClassName,
  bodyClassName,
  onHeaderPointerDown,
}: {
  title: ReactNode;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
  headerClassName?: string;
  bodyClassName?: string;
  onHeaderPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
}) {
  return (
    <div className={clsx('flex h-full min-h-0 flex-col bg-panel', className)}>
      <div
        className={clsx(
          'flex h-9 shrink-0 items-center justify-between border-b border-hairline px-3',
          headerClassName
        )}
        onPointerDown={onHeaderPointerDown}
      >
        <div className="flex min-w-0 items-center gap-0">
          {typeof title === 'string' ? (
            <span className="panel-title">{title}</span>
          ) : (
            title
          )}
        </div>
        {actions && <div className="flex items-center gap-1">{actions}</div>}
      </div>
      <div className={clsx('min-h-0 flex-1 overflow-auto', bodyClassName)}>{children}</div>
    </div>
  );
}

function displayValue(value: number, step: number, format?: (v: number) => string) {
  if (format) return format(value);
  if (step >= 1) return String(Math.round(value));
  if (step >= 0.1) return value.toFixed(1);
  return value.toFixed(2);
}

function parseTypedValue(raw: string): number | null {
  const cleaned = raw.trim().replace(/[^\d.eE+-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function snapToStep(n: number, min: number, step: number) {
  if (step <= 0) return n;
  const steps = Math.round((n - min) / step);
  return min + steps * step;
}

export function KeyframeDiamond({
  active,
  onClick,
  title,
}: {
  active?: boolean;
  onClick?: () => void;
  title?: string;
}) {
  if (!onClick) {
    return <span className="inline-block w-3.5 shrink-0" aria-hidden />;
  }
  return (
    <button
      type="button"
      title={title ?? (active ? 'Remove keyframe' : 'Add keyframe')}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={(e) => e.stopPropagation()}
      className={clsx(
        'inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center',
        active ? 'text-accent' : 'text-text-low hover:text-text-high'
      )}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
        <path
          d="M5 0.75 L9.25 5 L5 9.25 L0.75 5 Z"
          fill={active ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export function ScaleLinkButton({
  linked,
  onToggle,
}: {
  linked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center px-2 py-0.5">
      <span className="w-3.5 shrink-0" aria-hidden />
      <button
        type="button"
        title={linked ? 'Unlink scale X/Y' : 'Link scale X/Y'}
        onClick={onToggle}
        className={clsx(
          'ml-0 inline-flex h-5 w-[76px] items-center justify-center text-text-low hover:text-accent',
          linked && 'text-accent'
        )}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

export function SliderRow({
  label,
  value,
  min = -1,
  max = 1,
  step = 0.01,
  onChange,
  format,
  defaultValue = 0,
  keyframed,
  onToggleKeyframe,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
  /** Double-click label or slider to reset to this value (default 0). */
  defaultValue?: number;
  /** Filled diamond when a keyframe exists at the playhead. */
  keyframed?: boolean;
  onToggleKeyframe?: () => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState(() => displayValue(value, step, format));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(displayValue(value, step, format));
  }, [value, step, format, editing]);

  const reset = () => onChange(defaultValue);

  const commitDraft = () => {
    setEditing(false);
    const parsed = parseTypedValue(draft);
    if (parsed == null) {
      setDraft(displayValue(value, step, format));
      return;
    }
    const next = snapToStep(clamp(parsed, min, max), min, step);
    const rounded = step >= 1 ? Math.round(next) : Number(next.toFixed(6));
    onChange(rounded);
    setDraft(displayValue(rounded, step, format));
  };

  const pct = ((clamp(value, min, max) - min) / (max - min || 1)) * 100;

  return (
    <div
      className="grid grid-cols-[14px_76px_1fr_52px] items-center gap-2 px-2 py-[5px] text-2xs"
      title="Double-click label or track to reset"
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('input,button')) return;
        e.preventDefault();
        reset();
      }}
    >
      <KeyframeDiamond active={keyframed} onClick={onToggleKeyframe} />
      <label htmlFor={id} className="truncate text-text-low">
        {label}
      </label>
      <div className="cg-slider-wrap relative flex h-5 items-center">
        <div
          className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-[var(--cg-border-strong)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={clamp(value, min, max)}
          onChange={(e) => onChange(Number(e.target.value))}
          onPointerDown={() => useEditorStore.getState().beginHistoryTransaction()}
          onPointerUp={() => useEditorStore.getState().endHistoryTransaction()}
          onPointerCancel={() => useEditorStore.getState().endHistoryTransaction()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            reset();
          }}
          className="cg-slider relative z-[1] w-full"
        />
      </div>
      <input
        type="text"
        inputMode="decimal"
        aria-label={`${label} value`}
        title="Type a value · Enter to apply · ↑↓ to nudge"
        value={draft}
        onFocus={() => {
          setEditing(true);
          setDraft(
            step >= 1
              ? String(Math.round(value))
              : step >= 0.1
                ? value.toFixed(1)
                : value.toFixed(2)
          );
        }}
        onChange={(e) => {
          setEditing(true);
          setDraft(e.target.value);
        }}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setEditing(false);
            setDraft(displayValue(value, step, format));
            e.currentTarget.blur();
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const dir = e.key === 'ArrowUp' ? 1 : -1;
            const mul = e.shiftKey ? 10 : 1;
            const next = snapToStep(
              clamp(value + dir * step * mul, min, max),
              min,
              step
            );
            const rounded = step >= 1 ? Math.round(next) : Number(next.toFixed(6));
            onChange(rounded);
          }
        }}
        className="cg-slider-value timecode w-full"
      />
    </div>
  );
}

export function IconButton({
  active,
  title,
  onClick,
  children,
  danger,
  disabled,
}: {
  active?: boolean;
  title: string;
  onClick?: () => void;
  children: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex h-7 min-w-7 items-center justify-center rounded-lg px-2 text-2xs font-medium transition-colors',
        active && 'bg-raised text-accent',
        disabled && 'pointer-events-none opacity-35',
        !active && 'text-text-low hover:bg-raised hover:text-text-high',
        danger && 'hover:text-danger'
      )}
    >
      {children}
    </button>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="border-b border-hairline px-3 py-2 text-xs font-semibold text-text-high">
      {children}
    </div>
  );
}
